/**
 * Permission gates for pages and UI elements.
 *
 * DEVAMS-2025: this module is now a thin wrapper over `permissionsStore.isGranted()`.
 * All access decisions are permission-based; there is no role-based logic and no
 * CASL fallback. The store is populated synchronously before any gated route
 * renders by the permissions guard in `router/index.js`.
 *
 * Account-type roles (`ROLE_AI_AGENT`, `ROLE_DEVICE`) are gated on the BE only;
 * the FE has no role-aware code paths for normal users. `userInfo.roles[]` is
 * still received from the BE for backwards compatibility with stale mobile
 * clients but no FE access logic reads it.
 */
import { usePermissionsStore } from '@/stores/permissionsStore';
import useEmitter from '@/composables/useEmitter';
import type { PermissionOperation } from '@/types/permissions';
import { useRouter } from 'vue-router';

/**
 * Page-level guard. Call at the top of `<script setup>` on any gated page.
 *
 * If the user lacks `permission` for `operation`, emits a snackbar and redirects
 * to `/`. Otherwise no-op.
 *
 * The `permission` argument is a permission key from the registry exposed by
 * `GET /api/v1/admin/permission-registry` (e.g. `'admin_users'`, `'wpi'`,
 * `'flight_planner'`). Operations are `'read' | 'create' | 'update' | 'delete'`.
 */
export function checkAccessPage(permission: string, operation: PermissionOperation = 'read'): void {
    const permissionsStore = usePermissionsStore();
    if (permissionsStore.isGranted(permission, operation)) {
        return;
    }

    const router = useRouter();
    const emitter = useEmitter();
    emitter.emit('snackbar', {
        snackbar: true,
        snackbarTimeout: 6000,
        snackbarText: 'You don\'t have permission to view this page. Please contact your administrator if you believe this is an error.',
        snackIcon: 'mdi-minus-circle-outline',
        snackColor: 'red',
        snackClose: false,
    });
    router.push('/');
}

/**
 * Element-level visibility check. Returns true iff the user has `permission`
 * for `operation`. Use in `v-if` / computed visibility flags.
 */
export function checkVisibilityAllowed(permission: string, operation: PermissionOperation = 'read'): boolean {
    const permissionsStore = usePermissionsStore();
    return permissionsStore.isGranted(permission, operation);
}
